// Auto-Layout fuer Mindmap-Nodes. Horizontal Tree (Root links, Kinder rechts).
// Pure functions ohne Yjs-Abhaengigkeit.
//
// `placeSibling`/`placeChild` liefern eine Insert-Heuristik fuer die kurze Phase
// zwischen Edit und Auto-Layout (sonst flackert die UI). `computeMindmapLayout`
// nutzt `d3-hierarchy` (Reingold-Tilford-Tree) und ist der eigentliche Layout-Pfad.

import type { MindmapNodeObj } from "@ms-whiteboard/shared";
import { hierarchy, tree } from "d3-hierarchy";

export const MINDMAP_H_GAP = 80;
export const MINDMAP_V_GAP = 16;
export const MINDMAP_DEFAULT_W = 140;
export const MINDMAP_DEFAULT_H = 36;

export interface Point {
  readonly x: number;
  readonly y: number;
}

function widthOf(node: MindmapNodeObj): number {
  return node.width ?? MINDMAP_DEFAULT_W;
}

function heightOf(node: MindmapNodeObj): number {
  return node.height ?? MINDMAP_DEFAULT_H;
}

function childrenOf(
  parentId: string,
  allById: ReadonlyMap<string, MindmapNodeObj>,
): MindmapNodeObj[] {
  const out: MindmapNodeObj[] = [];
  for (const n of allById.values()) {
    if (n.parentNodeId === parentId) out.push(n);
  }
  out.sort((a, b) => a.y - b.y || a.x - b.x);
  return out;
}

/** Position fuer ein neues Child eines bestehenden Nodes. Reiht sich rechts ein, falls
 *  schon Kinder vorhanden direkt unter dem letzten Kind. */
export function placeChild(
  parent: MindmapNodeObj,
  allById: ReadonlyMap<string, MindmapNodeObj>,
): Point {
  const baseX = parent.x + widthOf(parent) + MINDMAP_H_GAP;
  const existing = childrenOf(parent.id, allById);
  const last = existing[existing.length - 1];
  if (!last) {
    return { x: baseX, y: parent.y };
  }
  return { x: baseX, y: last.y + heightOf(last) + MINDMAP_V_GAP };
}

/** Position fuer ein neues Sibling unter `source` (gleicher Parent). */
export function placeSibling(
  source: MindmapNodeObj,
  allById: ReadonlyMap<string, MindmapNodeObj>,
): Point {
  if (source.parentNodeId) {
    const parent = allById.get(source.parentNodeId);
    if (parent) {
      const siblings = childrenOf(parent.id, allById);
      const last = siblings[siblings.length - 1] ?? source;
      return { x: source.x, y: last.y + heightOf(last) + MINDMAP_V_GAP };
    }
  }
  // Root-Sibling: einfach unter den Source.
  return { x: source.x, y: source.y + heightOf(source) + MINDMAP_V_GAP };
}

/** Walked Tree zu Root, gibt Tiefe zurueck (Root = 0). Cycle-Guard via visited Set. */
export function levelOf(nodeId: string, allById: ReadonlyMap<string, MindmapNodeObj>): number {
  let depth = 0;
  let cur = allById.get(nodeId);
  const visited = new Set<string>();
  while (cur?.parentNodeId) {
    if (visited.has(cur.id)) return depth; // Cycle: abbrechen.
    visited.add(cur.id);
    const parent = allById.get(cur.parentNodeId);
    if (!parent) return depth;
    cur = parent;
    depth += 1;
  }
  return depth;
}

/** Wuerde das Setzen `node.parentNodeId = newParentId` einen Zyklus erzeugen? */
export function wouldCreateCycle(
  nodeId: string,
  newParentId: string | null,
  allById: ReadonlyMap<string, MindmapNodeObj>,
): boolean {
  if (!newParentId) return false;
  if (nodeId === newParentId) return true;
  let cur = allById.get(newParentId);
  const visited = new Set<string>();
  while (cur?.parentNodeId) {
    if (visited.has(cur.id)) return false;
    visited.add(cur.id);
    if (cur.parentNodeId === nodeId) return true;
    cur = allById.get(cur.parentNodeId);
  }
  return false;
}

/** Voller Subtree-Layout via d3-hierarchy (Reingold-Tilford-Tree). Horizontal-Ausrichtung:
 *  Root in der Mitte, Top-Level-Children werden alternierend links und rechts verteilt
 *  (XMind-balanced-map). Subtree-Hoehen werden balanciert.
 *  Positions-Output ist absolut in Welt-Koordinaten, Origin = aktueller Root-Position. */
export function computeMindmapLayout(
  rootId: string,
  allById: ReadonlyMap<string, MindmapNodeObj>,
  gaps: { readonly h: number; readonly v: number } = { h: MINDMAP_H_GAP, v: MINDMAP_V_GAP },
): Map<string, Point> {
  const out = new Map<string, Point>();
  const rootObj = allById.get(rootId);
  if (!rootObj) return out;

  // Root selbst behaelt seine Welt-Position.
  out.set(rootObj.id, { x: rootObj.x, y: rootObj.y });

  // Top-Level-Children alternierend in zwei Gruppen — Index 0,2,4 → rechts; 1,3,5 → links.
  // Liefert symmetrische XMind-Optik.
  const topChildren = childrenOf(rootObj.id, allById);
  const rightChildren: MindmapNodeObj[] = [];
  const leftChildren: MindmapNodeObj[] = [];
  topChildren.forEach((c, i) => {
    if (i % 2 === 0) rightChildren.push(c);
    else leftChildren.push(c);
  });

  const visited = new Set<string>([rootObj.id]);
  layoutSide(rootObj, rightChildren, false, allById, gaps, visited, out);
  layoutSide(rootObj, leftChildren, true, allById, gaps, visited, out);

  return out;
}

function layoutSide(
  rootObj: MindmapNodeObj,
  topChildren: MindmapNodeObj[],
  mirror: boolean,
  allById: ReadonlyMap<string, MindmapNodeObj>,
  gaps: { readonly h: number; readonly v: number },
  visited: Set<string>,
  out: Map<string, Point>,
): void {
  if (topChildren.length === 0) return;

  // Cycle-safe Walk: visited shared zwischen left/right, damit derselbe Knoten nicht
  // doppelt platziert wird (auch wenn das in normalen Mindmap-Trees nicht vorkommen sollte).
  const sideRoot = hierarchy<MindmapNodeObj>(rootObj, (n) => {
    if (n.id === rootObj.id) {
      // Auf dem virtuellen Side-Root nur die zugewiesenen Top-Children weitergeben.
      const filtered = topChildren.filter((c) => !visited.has(c.id));
      for (const c of filtered) visited.add(c.id);
      return filtered;
    }
    if (visited.has(`${n.id}::descended`)) return [];
    visited.add(`${n.id}::descended`);
    const children = childrenOf(n.id, allById).filter((c) => !visited.has(c.id));
    for (const c of children) visited.add(c.id);
    return children;
  });

  const layout = tree<MindmapNodeObj>().nodeSize([
    MINDMAP_DEFAULT_H + gaps.v,
    MINDMAP_DEFAULT_W + gaps.h,
  ]);
  layout(sideRoot);

  for (const node of sideRoot.descendants()) {
    if (node.data.id === rootObj.id) continue; // Root nicht ueberschreiben
    const nx = node.x ?? 0;
    const ny = node.y ?? 0;
    out.set(node.data.id, {
      x: rootObj.x + (mirror ? -ny : ny),
      y: rootObj.y + nx,
    });
  }
}

/** Berechnet Layout fuer den Subtree um `anchorNodeId` und schreibt die Positionen
 *  in einer Yjs-Transact in `yMap`. Wenn `anchorNodeId` kein mindmap-node oder die
 *  Root nicht findbar ist, ist die Operation ein No-Op. Idempotent — gleiche Eingabe
 *  fuehrt zu denselben Positionen. */
export function applyMindmapLayout(
  yMap: {
    values(): Iterable<unknown>;
    set(key: string, value: unknown): void;
    doc?: { transact(fn: () => void): void } | null;
  },
  anchorNodeId: string,
): void {
  const byId = new Map<string, MindmapNodeObj>();
  for (const v of yMap.values()) {
    if (v && typeof v === "object" && (v as { type?: unknown }).type === "mindmap-node") {
      const node = v as MindmapNodeObj;
      byId.set(node.id, node);
    }
  }
  const rootId = findMindmapRoot(anchorNodeId, byId);
  if (!rootId) return;
  const positions = computeMindmapLayout(rootId, byId);
  if (positions.size === 0) return;
  const op = (): void => {
    for (const [id, pos] of positions) {
      const node = byId.get(id);
      if (!node) continue;
      if (node.x === pos.x && node.y === pos.y) continue;
      yMap.set(id, { ...node, x: pos.x, y: pos.y });
    }
  };
  if (yMap.doc) yMap.doc.transact(op);
  else op();
}

/** Hit-Test fuer Drag-Re-Parent: gibt den Mindmap-Node zurueck, dessen Bounds den
 *  Drop-Punkt enthalten, sofern Re-Parent zulaessig ist (kein Self, kein Cycle).
 *  Drop-Punkt sollte der Mittelpunkt des gedraggten Nodes nach dem Move sein. */
export function computeDropTarget(
  draggedId: string,
  dropCenterX: number,
  dropCenterY: number,
  byId: ReadonlyMap<string, MindmapNodeObj>,
): string | null {
  for (const node of byId.values()) {
    if (node.id === draggedId) continue;
    const w = widthOf(node);
    const h = heightOf(node);
    if (
      dropCenterX < node.x ||
      dropCenterX > node.x + w ||
      dropCenterY < node.y ||
      dropCenterY > node.y + h
    ) {
      continue;
    }
    if (wouldCreateCycle(draggedId, node.id, byId)) continue;
    return node.id;
  }
  return null;
}

/** Walked von einem beliebigen Mindmap-Node zur Tree-Root (parentNodeId === null). */
export function findMindmapRoot(
  startId: string,
  allById: ReadonlyMap<string, MindmapNodeObj>,
): string | null {
  let cur = allById.get(startId);
  if (!cur) return null;
  const visited = new Set<string>();
  while (cur.parentNodeId) {
    if (visited.has(cur.id)) return cur.id;
    visited.add(cur.id);
    const parent = allById.get(cur.parentNodeId);
    if (!parent) return cur.id;
    cur = parent;
  }
  return cur.id;
}
